import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, Input, Switch, message, Popconfirm, Space, Typography } from 'antd';
import AdaptiveTable from '../components/AdaptiveTable';
import { carrierApi, type CarrierDTO } from '../api/kfc';
import { clientTablePagination } from '../utils/tablePagination';
import CarrierLogo from '../components/CarrierLogo';

const TRACKING_PLACEHOLDER = '{trackingNo}';
const VESSEL_PLACEHOLDERS = ['{vesselImo}', '{vesselMmsi}', '{vesselName}'];

const buildTrackingUrl = (template: string, trackingNo: string) =>
  template
    .trim()
    .split(TRACKING_PLACEHOLDER).join(encodeURIComponent(trackingNo.trim()))
    // 船占位符用示例船 LURLINE 渲染，便于点击测试
    .split('{vesselImo}').join('9814600')
    .split('{vesselMmsi}').join('338121000')
    .split('{vesselName}').join('LURLINE');

const hasAnyPlaceholder = (template: string) =>
  template.includes(TRACKING_PLACEHOLDER) || VESSEL_PLACEHOLDERS.some((p) => template.includes(p));

const CarrierManage: React.FC = () => {
  const [list, setList] = useState<CarrierDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [sampleTrackingNo, setSampleTrackingNo] = useState('');
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    carrierApi
      .list()
      .then((res) => setList(res.data))
      .catch(() => {
        setList([]);
        message.warning('无法加载承运商列表');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setSampleTrackingNo('');
    form.resetFields();
    form.setFieldsValue({ isActive: true });
    setModalOpen(true);
  };

  const openEdit = (r: CarrierDTO) => {
    setEditingId(r.id);
    setSampleTrackingNo('');
    form.setFieldsValue({
      carrierCode: r.carrierCode,
      carrierName: r.carrierName,
      remark: r.remark,
      trackingUrlTemplate: r.trackingUrlTemplate,
      isActive: r.isActive !== false,
    });
    setModalOpen(true);
  };

  const onFinish = (values: Record<string, unknown>) => {
    const payload: Partial<CarrierDTO> = {
      carrierCode: values.carrierCode as string,
      carrierName: values.carrierName as string,
      remark: values.remark as string | undefined,
      trackingUrlTemplate: ((values.trackingUrlTemplate as string | undefined) ?? '').trim(),
      isActive: values.isActive as boolean,
    };
    const submit = editingId ? carrierApi.update(editingId, payload) : carrierApi.create(payload);
    submit
      .then(() => {
        message.success(editingId ? '更新成功' : '创建成功');
        setModalOpen(false);
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'));
  };

  const onTestTemplate = () => {
    const template = ((form.getFieldValue('trackingUrlTemplate') as string | undefined) ?? '').trim();
    if (!template || !hasAnyPlaceholder(template)) {
      message.warning(`请先填写包含 ${TRACKING_PLACEHOLDER} 或船占位符的轨迹查询地址`);
      return;
    }
    if (template.includes(TRACKING_PLACEHOLDER) && !sampleTrackingNo.trim()) {
      message.warning('请输入示例转单号');
      return;
    }
    window.open(buildTrackingUrl(template, sampleTrackingNo), '_blank', 'noopener,noreferrer');
  };

  const onDelete = (id: string) => {
    carrierApi
      .delete(id)
      .then(() => {
        message.success('已删除');
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '删除失败'));
  };

  return (
    <>
      <Card title="承运商列表" extra={<Button type="primary" onClick={openAdd}>新增承运商</Button>}>
        <AdaptiveTable
          loading={loading}
          dataSource={list}
          rowKey="id"
          pagination={clientTablePagination()}
          columns={[
            { title: '编码', dataIndex: 'carrierCode', width: 140 },
            {
              title: '名称',
              dataIndex: 'carrierName',
              width: 200,
              ellipsis: true,
              render: (v: string | undefined, r) => (
                <Space size={6}>
                  <CarrierLogo code={r.carrierCode} name={v} height={16} />
                  <span>{v || '-'}</span>
                </Space>
              ),
            },
            {
              title: '轨迹查询地址',
              dataIndex: 'trackingUrlTemplate',
              ellipsis: true,
              render: (v?: string) =>
                v ? (
                  <Typography.Text ellipsis={{ tooltip: v }} style={{ maxWidth: '100%' }}>
                    {v}
                  </Typography.Text>
                ) : (
                  '-'
                ),
            },
            { title: '备注', dataIndex: 'remark', ellipsis: true },
            {
              title: '启用',
              dataIndex: 'isActive',
              width: 80,
              render: (v: boolean) => (v !== false ? '是' : '否'),
            },
            {
              title: '操作',
              width: 140,
              render: (_, r) => (
                <Space>
                  <Button type="link" size="small" onClick={() => openEdit(r)}>
                    编辑
                  </Button>
                  <Popconfirm title="确定删除该承运商？" onConfirm={() => onDelete(r.id)}>
                    <Button type="link" size="small" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editingId ? '编辑承运商' : '新增承运商'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
        width={640}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="carrierCode" label="承运商编码" rules={[{ required: true, message: '请输入编码' }]}>
            <Input disabled={!!editingId} />
          </Form.Item>
          <Form.Item name="carrierName" label="承运商名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="trackingUrlTemplate"
            label="轨迹查询地址"
            extra={`用 ${TRACKING_PLACEHOLDER} 代表转单号（如 https://www.ups.com/track?loc=zh_CN&tracknum=${TRACKING_PLACEHOLDER}）；海运可用 {vesselImo}/{vesselMmsi}/{vesselName} 代表船字段（如 https://www.myshiptracking.com/vessels/vessel-mmsi-{vesselMmsi}-imo-{vesselImo}）`}
            rules={[
              { max: 500, message: '最长 500 字' },
              {
                validator: (_, v?: string) => {
                  const t = (v ?? '').trim();
                  if (!t) return Promise.resolve();
                  if (!/^https?:\/\//i.test(t)) {
                    return Promise.reject(new Error('须以 http:// 或 https:// 开头'));
                  }
                  if (!hasAnyPlaceholder(t)) {
                    return Promise.reject(new Error(`须包含 ${TRACKING_PLACEHOLDER} 或 {vesselImo}/{vesselMmsi}/{vesselName}`));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input allowClear placeholder={`https://...${TRACKING_PLACEHOLDER}`} />
          </Form.Item>
          <Form.Item label="测试轨迹查询地址">
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={sampleTrackingNo}
                onChange={(e) => setSampleTrackingNo(e.target.value)}
                placeholder="输入示例转单号"
                onPressEnter={(e) => {
                  e.preventDefault();
                  onTestTemplate();
                }}
              />
              <Button onClick={onTestTemplate}>测试</Button>
            </Space.Compact>
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input />
          </Form.Item>
          <Form.Item name="isActive" label="启用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              保存
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setModalOpen(false)}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CarrierManage;
